import { Fragment } from 'react';
import { Badge } from '../../../components/ui/Badge';
import { Modal } from '../../../components/ui/Modal';
import type { StepRow } from '../../../api/outreach';
import { formatDateTime, stepTypeLabel } from './format';

/** Splits a template on `{{variable}}` so the variables can be highlighted in place. */
function HighlightedTemplate({ text }: { text: string }) {
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return (
    <p className="whitespace-pre-wrap text-body-sm text-text-primary">
      {parts.map((part, index) =>
        /^\{\{[^}]+\}\}$/.test(part) ? (
          <span key={index} className="rounded bg-accent/10 px-1 font-mono text-body-xs text-accent">
            {part}
          </span>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </p>
  );
}

function TemplateBlock({ title, text }: { title: string; text: string }) {
  return (
    <div className="rounded-lg border border-border-light bg-surface-secondary p-3">
      <p className="mb-2 text-body-xs uppercase tracking-wide text-text-tertiary">{title}</p>
      <HighlightedTemplate text={text} />
    </div>
  );
}

/**
 * The copy behind one sequence step: the template lemlist picks from (message,
 * altMessage, altMessagePremium), the variables it uses, and the last three
 * messages that were actually rendered and sent — where a variable that renders
 * blank becomes visible.
 */
export function CopyDrawer({ step, onClose }: { step: StepRow | null; onClose: () => void }) {
  const templates: { title: string; text: string }[] = [];
  if (step?.copy) {
    const candidates: [string, string | null][] = [
      ['Message', step.copy.message],
      ['Alternative message', step.copy.altMessage],
      ['Alternative message (Premium seat)', step.copy.altMessagePremium],
    ];
    for (const [title, text] of candidates) if (text) templates.push({ title, text });
  }

  return (
    <Modal
      isOpen={step !== null}
      onClose={onClose}
      size="full"
      title={step ? `Step ${step.index} · ${stepTypeLabel(step.type)}` : undefined}
      description={
        step
          ? [step.branchLabel ? `Branch: ${step.branchLabel}` : 'Main sequence', `delay ${step.delayDays}d`].join(' · ')
          : undefined
      }
    >
      {step && (
        <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
          <section className="space-y-2">
            <h4 className="text-body-sm font-medium text-text-secondary">Template</h4>
            {templates.length === 0 ? (
              <p className="text-body-sm text-text-tertiary">
                {step.copy ? 'This step has no message text (a visit, invite without note, or condition).' : 'No copy synced for this step.'}
              </p>
            ) : (
              <>
                {templates.length > 1 && (
                  <p className="text-body-xs text-text-tertiary">lemlist picks one of these per lead.</p>
                )}
                {templates.map(({ title, text }) => (
                  <TemplateBlock key={title} title={title} text={text} />
                ))}
              </>
            )}
          </section>

          {step.copy && step.copy.variables.length > 0 && (
            <section className="space-y-2">
              <h4 className="text-body-sm font-medium text-text-secondary">Variables used</h4>
              <div className="flex flex-wrap gap-1">
                {step.copy.variables.map((variable) => (
                  <Badge key={variable}>
                    <span className="font-mono">{`{{${variable.replace(/^\{\{|\}\}$/g, '')}}}`}</span>
                  </Badge>
                ))}
              </div>
            </section>
          )}

          <section className="space-y-2">
            <h4 className="text-body-sm font-medium text-text-secondary">Last messages actually sent</h4>
            {step.renderedSamples.length === 0 ? (
              <p className="text-body-sm text-text-tertiary">Nothing sent from this step yet.</p>
            ) : (
              step.renderedSamples.map((sample) => (
                <div key={`${sample.leadId}-${sample.sentAt}`} className="rounded-lg border border-border-light p-3">
                  <p className="mb-2 text-body-xs text-text-tertiary">
                    To {sample.leadFirstName ?? 'unnamed lead'} · {formatDateTime(sample.sentAt)}
                  </p>
                  <p className="whitespace-pre-wrap text-body-sm text-text-primary">{sample.message}</p>
                </div>
              ))
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}
